import { api } from "@/lib/api";
import { formatDate } from "@/lib/utils";
import {
  Badge,
  Button,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@a7y/tailwind-catalyst";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { BookOpen, Megaphone, Pencil, Plus, ToggleLeft, ToggleRight } from "lucide-react";
import { useState } from "react";
import { TopicFormDialog } from "./TopicFormDialog";
import { BroadcastConfirmDialog } from "./BroadcastConfirmDialog";
import { ActivateTopicConfirmDialog } from "./ActivateTopicConfirmDialog";
import type { LearningTopicDoc } from "@a7y/types";

const FEATURE_LABELS: Record<string, string> = {
  audit: "Audit",
  project: "Project",
  store_credential: "Store Credential",
  scheduled_audit: "Scheduled Audit",
  keyword_tracking: "Keyword Tracking",
  analytics: "Analytics",
  translation: "Translation",
  publishing: "Publishing",
};

export function TopicListTab() {
  const queryClient = useQueryClient();
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [editingTopic, setEditingTopic] = useState<LearningTopicDoc | null>(null);
  const [broadcastTopic, setBroadcastTopic] = useState<LearningTopicDoc | null>(null);
  const [activatingTopic, setActivatingTopic] = useState<LearningTopicDoc | null>(null);
  const [statusMessage, setStatusMessage] = useState<{
    type: "success" | "error";
    text: string;
  } | null>(null);

  const showStatus = (type: "success" | "error", text: string) => {
    setStatusMessage({ type, text });
    setTimeout(() => setStatusMessage(null), 4000);
  };

  const { data, isLoading } = useQuery({
    queryKey: ["learning-topics"],
    queryFn: () => api.getLearningTopics(),
  });

  const topics: LearningTopicDoc[] = data ?? [];

  const toggleActiveMutation = useMutation({
    mutationFn: ({ id, active }: { id: string; active: boolean }) =>
      api.updateLearningTopic(id, { active }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["learning-topics"] });
      showStatus("success", "Topic updated");
    },
    onError: () => showStatus("error", "Failed to update topic"),
  });

  // Switching a topic on asks first (it is emailed at the next scheduled run);
  // switching off applies at once.
  const handleToggleActive = (topic: LearningTopicDoc) => {
    if (topic.active) {
      toggleActiveMutation.mutate({ id: topic.id, active: false });
    } else {
      setActivatingTopic(topic);
    }
  };

  const handleConfirmActivate = () => {
    if (activatingTopic) {
      toggleActiveMutation.mutate({ id: activatingTopic.id, active: true });
    }
    setActivatingTopic(null);
  };

  const handleEdit = (topic: LearningTopicDoc) => {
    setEditingTopic(topic);
    setIsFormOpen(true);
  };

  const handleAdd = () => {
    setEditingTopic(null);
    setIsFormOpen(true);
  };

  const handleFormClose = () => {
    setIsFormOpen(false);
    setEditingTopic(null);
  };

  const handleFormSaved = () => {
    queryClient.invalidateQueries({ queryKey: ["learning-topics"] });
    handleFormClose();
    showStatus("success", editingTopic ? "Topic updated" : "Topic created");
  };

  const handleBroadcastComplete = () => {
    setBroadcastTopic(null);
    queryClient.invalidateQueries({ queryKey: ["learning-topics"] });
  };

  return (
    <>
      {/* Header with Add button */}
      <div className="mb-4 flex items-center justify-between">
        <div />
        <Button color="blue" onClick={handleAdd}>
          <Plus className="h-4 w-4" />
          Add Topic
        </Button>
      </div>

      {/* Status banner */}
      {statusMessage && (
        <div
          className={`mb-4 rounded-lg px-4 py-2.5 text-sm font-medium ${
            statusMessage.type === "success"
              ? "bg-green-50 text-green-700 dark:bg-green-900/20 dark:text-green-400"
              : "bg-red-50 text-red-700 dark:bg-red-900/20 dark:text-red-400"
          }`}
        >
          {statusMessage.text}
        </div>
      )}

      {/* Table */}
      <Table dense striped>
        <TableHead>
          <TableRow>
            <TableHeader>Title</TableHeader>
            <TableHeader>Feature Taught</TableHeader>
            <TableHeader>Lifecycle Stages</TableHeader>
            <TableHeader>Priority</TableHeader>
            <TableHeader>Status</TableHeader>
            <TableHeader>Created</TableHeader>
            <TableHeader className="text-right">Actions</TableHeader>
          </TableRow>
        </TableHead>
        <TableBody>
          {isLoading ? (
            <TableRow>
              <TableCell colSpan={7} className="text-center">
                <div className="mx-auto h-6 w-6 animate-spin rounded-full border-4 border-gray-300 border-t-blue-600 dark:border-zinc-600" />
              </TableCell>
            </TableRow>
          ) : topics.length === 0 ? (
            <TableRow>
              <TableCell colSpan={7} className="py-8 text-center">
                <BookOpen className="mx-auto h-12 w-12 text-zinc-400" />
                <p className="mt-2 text-zinc-500">No learning topics yet</p>
              </TableCell>
            </TableRow>
          ) : (
            topics.map((topic) => (
              <TableRow
                key={topic.id}
                className="cursor-pointer"
                onClick={() => handleEdit(topic)}
              >
                <TableCell className="font-medium">{topic.title}</TableCell>
                <TableCell>
                  <Badge color="blue">
                    {FEATURE_LABELS[topic.featureTaught] ?? topic.featureTaught}
                  </Badge>
                </TableCell>
                <TableCell>
                  <div className="flex flex-wrap gap-1">
                    {topic.targetLifecycleStages.map((stage) => (
                      <Badge key={stage} color="zinc">
                        {stage.replace("_", " ")}
                      </Badge>
                    ))}
                  </div>
                </TableCell>
                <TableCell>{topic.priority}</TableCell>
                <TableCell>
                  <Badge color={topic.active ? "green" : "zinc"}>
                    {topic.active ? "Active" : "Inactive"}
                  </Badge>
                </TableCell>
                <TableCell className="text-zinc-500">
                  {formatDate(topic.createdAt)}
                </TableCell>
                <TableCell className="text-right">
                  <div
                    className="flex items-center justify-end gap-1"
                    onClick={(e) => e.stopPropagation()}
                  >
                    <Button
                      plain
                      onClick={() => handleEdit(topic)}
                      title="Edit"
                    >
                      <Pencil className="h-4 w-4 text-blue-400" />
                    </Button>
                    <Button
                      plain
                      onClick={() => handleToggleActive(topic)}
                      disabled={toggleActiveMutation.isPending}
                      title={topic.active ? "Deactivate" : "Activate"}
                    >
                      {topic.active ? (
                        <ToggleRight className="h-4 w-4 text-green-500" />
                      ) : (
                        <ToggleLeft className="h-4 w-4 text-zinc-400" />
                      )}
                    </Button>
                    {topic.active && (
                      <Button
                        plain
                        onClick={() => setBroadcastTopic(topic)}
                        title="Broadcast"
                      >
                        <Megaphone className="h-4 w-4 text-amber-500" />
                      </Button>
                    )}
                  </div>
                </TableCell>
              </TableRow>
            ))
          )}
        </TableBody>
      </Table>

      {/* Dialogs */}
      <TopicFormDialog
        open={isFormOpen}
        onClose={handleFormClose}
        onSaved={handleFormSaved}
        topic={editingTopic}
      />

      <ActivateTopicConfirmDialog
        open={!!activatingTopic}
        topicTitle={activatingTopic?.title}
        onCancel={() => setActivatingTopic(null)}
        onConfirm={handleConfirmActivate}
      />

      {broadcastTopic && (
        <BroadcastConfirmDialog
          open={!!broadcastTopic}
          onClose={() => setBroadcastTopic(null)}
          topic={broadcastTopic}
          onComplete={handleBroadcastComplete}
        />
      )}
    </>
  );
}
